import type { AmenityData, SchoolItem, RestaurantItem } from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const MAX_SCHOOLS = 8
const MAX_PARKS = 6
const MAX_RESTAURANTS = 6

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function SchoolBadge({ type }: { type?: string }) {
  if (!type) return null

  const label = type === "independent" ? "Independent" : "State"
  const className =
    type === "independent"
      ? "rounded-full bg-stone px-2 py-0.5 text-[10px] font-medium text-charcoal"
      : "rounded-full bg-charcoal/5 px-2 py-0.5 text-[10px] font-medium text-charcoal"

  return <span className={className}>{label}</span>
}

function SchoolsSection({ schools }: { schools: SchoolItem[] }) {
  const visible = schools.slice(0, MAX_SCHOOLS)
  const remaining = schools.length - visible.length

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate">
        Schools ({schools.length})
      </p>
      <ul className="mt-2 space-y-1.5">
        {visible.map((school) => (
          <li
            key={`${school.name}-${school.type ?? "unknown"}`}
            className="flex items-center gap-2 text-sm text-charcoal"
          >
            <span className="truncate">{school.name}</span>
            <SchoolBadge type={school.type} />
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="mt-1.5 text-xs text-slate">
          and {remaining} more
        </p>
      )}
    </div>
  )
}

function ParksSection({ parks }: { parks: { name: string }[] }) {
  const visible = parks.slice(0, MAX_PARKS)
  const remaining = parks.length - visible.length

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate">
        Parks and green spaces ({parks.length})
      </p>
      <ul className="mt-2 space-y-1.5">
        {visible.map((park) => (
          <li key={park.name} className="text-sm text-charcoal truncate">
            {park.name}
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="mt-1.5 text-xs text-slate">
          and {remaining} more
        </p>
      )}
    </div>
  )
}

function RestaurantsSection({ restaurants }: { restaurants: RestaurantItem[] }) {
  const visible = restaurants.slice(0, MAX_RESTAURANTS)
  const remaining = restaurants.length - visible.length

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate">
        Restaurants ({restaurants.length})
      </p>
      <ul className="mt-2 space-y-1.5">
        {visible.map((r) => (
          <li key={`${r.name}-${r.cuisine ?? ""}`} className="text-sm text-charcoal">
            <span className="truncate">{r.name}</span>
            {r.cuisine && (
              <span className="ml-1.5 text-xs text-slate">({r.cuisine})</span>
            )}
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="mt-1.5 text-xs text-slate">
          and {remaining} more
        </p>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Main card                                                           */
/* ------------------------------------------------------------------ */

interface AmenitiesCardProps {
  data: AmenityData
}

export default function AmenitiesCard({ data }: AmenitiesCardProps) {
  return (
    <div className="rounded-xl border border-stone bg-white p-6">
      <h3 className="font-display text-lg font-semibold text-charcoal">
        Local Amenities
      </h3>

      <div className="mt-5 space-y-6">
        {data.schools.length > 0 && <SchoolsSection schools={data.schools} />}
        {data.parks.length > 0 && <ParksSection parks={data.parks} />}
        {data.restaurants.length > 0 && (
          <RestaurantsSection restaurants={data.restaurants} />
        )}
      </div>

      {/* Compact counts row for remaining numeric amenities */}
      <div className="mt-6 grid grid-cols-2 gap-4 border-t border-stone pt-4">
        <div>
          <p className="text-2xl font-bold text-charcoal">{data.shops}</p>
          <p className="text-sm font-medium text-slate">Shops</p>
          <p className="text-xs text-slate/70">within 0.5 miles</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-charcoal">{data.transportStops}</p>
          <p className="text-sm font-medium text-slate">Transport stops</p>
          <p className="text-xs text-slate/70">within 0.5 miles</p>
        </div>
      </div>
    </div>
  )
}
